import { useCallback, useEffect, useState } from "react";
import DataTable, { Column, FilterOption } from "../../components/commons/DataTable";
import CourseAction from "../../components/partials/course/CourseAction";
import type { Course, SearchCourseParams } from "../../services/webServices/course/types";
import { getCourses } from "../../services/webServices/course/get";
import { useAdminLoading } from "../../context/AdminLoadingContext";
import { useInitialPageLoad } from "../../components/layouts/admin-layout/useInitialPageLoad";

const PAGE_SIZE = 12;

const filterOptions: FilterOption[] = [
  { value: "draft", label: "پیش نویس" },
  { value: "publishing_soon_approval", label: "در انتظار تایید(بزودی)" },
  { value: "publishing_soon", label: "بزودی" },
  { value: "published_approval", label: "در انتظار تایید" },
  { value: "published", label: "منتشر شده" },
];

export default function Course() {

  const [data, setData] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [filterValue, setFilterValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);

  const { showLoading, hideLoading } = useAdminLoading();
  const isFirstLoad = useInitialPageLoad();

  const fetchCourse = useCallback(async () => {

    if (isFirstLoad) showLoading();
    setIsLoading(true);

    const params: SearchCourseParams = {
      limit: PAGE_SIZE,
      offset: (currentPage - 1) * PAGE_SIZE,
    };

    if (searchValue) params.search = searchValue;

    switch (filterValue) {
      case "draft":
        params.publish_status = 1;
        break;
      case "publishing_soon_approval":
        params.publish_status = 2;
        break;
      case "publishing_soon":
        params.publish_status = 3;
        break;
      case "published_approval":
        params.publish_status = 4;
        break;
      case "published":
        params.publish_status = 5;
        break;
    }

    const response = await getCourses({ params });

    const mappedResponse = response.map((item) => ({
      ...item,
      full_name: item.user?.full_name,
    }));

    setData(mappedResponse);

    if (response.length < PAGE_SIZE) {
      setTotalItems((currentPage - 1) * PAGE_SIZE + response.length);
    } else {
      setTotalItems(currentPage * PAGE_SIZE + 1);
    }

    setIsLoading(false);
    if (isFirstLoad) hideLoading();
  }, [currentPage, searchValue, filterValue]);

  useEffect(() => {
    fetchCourse();
  }, [fetchCourse]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (value: string) => {
    setFilterValue(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const handleOnRowClick = (row: Course) => {
    window.open(`https://sepehrjou.ir/course/djc-${row.id}/دوره`, "_blank");
  };

  const handleRenderActions = (row: Course) => {
    return (
      <CourseAction
        course={row}
        onActionSuccessed={() => fetchCourse()}
      />
    );
  };

  const columns: Column<Course>[] = [
    {
      key: "title",
      header: "عنوان",
      align: "right",
      sortable: true,
      width: "45%",
      accessor: (row) => row.title,
    },
    {
      key: "full_name",
      header: "برگزار کننده",
      align: "right",
      sortable: true,
      width: "25%",
      accessor: (row) => (
        <div className="flex items-center gap-2">
          <img
            className="h-[20px] w-[20px] rounded-full object-cover"
            src={process.env.REACT_APP_File_URL + row.user?.avatar || "/images/avatar-alt.jpg"}
            alt={row.user?.full_name || "بدون نام"}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/images/avatar-alt.jpg";
            }}
          />
          <span>{row.user?.full_name || "بدون نام"}</span>
        </div>
      ),
    },
    {
      key: "created_at",
      header: "زمان انتشار",
      align: "right",
      sortable: true,
      width: "15%",
      accessor: (row) => (
        <div className="flex items-center gap-2">
          <span>
            {row.created_at
              ? new Date(row.created_at).toLocaleDateString("fa-IR")
              : "بدون تاریخ"}
          </span>
        </div>
      ),
    }
  ];

  return (
    <div>
      <DataTable
        title="لیست دوره‌ها"
        data={data}
        columns={columns}
        loading={isLoading}
        loadingText="در حال بارگذاری دوره‌ها..."
        emptyText="دوره‌ای یافت نشد"

        searchable={true}
        searchPlaceholder="عنوان"
        searchKeys={["title"]}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}

        filterable={true}
        filterPlaceholder="همه دوره‌ها"
        filterOptions={filterOptions}
        filterValue={filterValue}
        onFilterChange={handleFilterChange}

        pagination={true}
        serverSidePagination={true}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        totalItems={totalItems}
        pageSize={PAGE_SIZE}

        onRowClick={handleOnRowClick}
        renderActions={handleRenderActions}
      />
    </div>
  );
};
